import { SearchCombobox } from '@/features/shared/components/search-combobox/search-combobox';
import { getFullAddressString } from '@/lib/address-helper';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerAddressDTO } from '@/server/dto/customer-address-dto';
import type { SalesAddress } from '@/server/types/drafts-types';
import { api } from '@/utils/api';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { pickBy } from 'lodash-es';
import { useMemo } from 'react';
import { useController, useWatch, type FieldValues, type UseControllerProps } from 'react-hook-form';

type FormShippingAddressFieldProps<T extends FieldValues> = {
    placeholder?: string;
} & UseControllerProps<T>;

export const toSalesAddress = (address: CustomerAddressDTO): SalesAddress => ({
    address_id: address.addressId,
    street: address.street,
    city: address.city,
    country: address.country,
    country_code: address.countryCode,
    line1: address.addressLine1,
    line2: address.addressLine2,
    line3: address.addressLine3,
    zip: address.zipCode,
});

export const FormShippingAddressField = <T extends FieldValues>(props: FormShippingAddressFieldProps<T>) => {
    const { t } = useLocale('sales-draft');
    const { field } = useController(props);

    const value = field.value as SalesAddress;

    const customerId = useWatch({
        name: 'customer_id',
    });

    const { data: rawData, isLoading } = api.platoUser.getManyCustomerAddress.get.useQuery({
        customerId: customerId ?? '',
        types: [SrCustomerAddressType.SHIPPING],
    });

    const data = useMemo(() => {
        return rawData?.map((item) => toSalesAddress(item));
    }, [rawData]);

    const groupedItems = useMemo(() => {
        return [
            {
                id: '1',
                items: data || [],
            },
        ];
    }, [data]);

    return (
        <SearchCombobox<SalesAddress>
            className="w-full"
            isLoading={isLoading}
            groupedItems={groupedItems}
            notFoundPlaceholder={''}
            inputPlaceholder={t('sales_draft__header__shipping_address', {
                ns: 'sales-draft',
            })}
            keySelector={(item) => item.address_id || ''}
            valueSelector={(item) => item.address_id || ''}
            renderItem={(item) => {
                return !!item
                    ? getFullAddressString({
                          addressLine1: item.line1 ?? undefined,
                          addressLine2: item.line2 ?? undefined,
                          addressLine3: item.line3 ?? undefined,
                          street: item.street ?? undefined,
                          zipCode: item.zip ?? undefined,
                          city: item.city ?? undefined,
                          country: item.country ?? undefined,
                      })
                    : '';
            }}
            onValueChange={(id) => {
                const address = data?.find((item) => item.address_id === id);

                field.onChange(pickBy(address, Boolean));
            }}
            selectedValue={value?.address_id || ''}
            disabled={field.disabled}
        />
    );
};
